import React from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import SyncAltIcon from 'components/material-symbol/icons/SyncAlt'
import AddOutlinedIcon from 'components/material-symbol/icons/Add'
import { makeStyles } from '@material-ui/core/styles'
import { DateTime } from 'luxon'
import useIsMobile from 'lib/hooks/use-is-mobile'
import {
  HOUR_HEIGHT,
  HOUR_HEIGHT_MOBILE,
  dayLabels,
  formatHourLabel,
  buildRecurringByDayIndex,
  buildOverridesByDateKey,
  buildBlockedByDay,
  computeVisibleHourRange,
} from './utils'

const HOUR_LABEL_WIDTH = 48
const HOUR_LABEL_WIDTH_MOBILE = 36

const useStyles = makeStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: `${HOUR_LABEL_WIDTH}px repeat(7, 1fr)`,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.only('xs')]: {
      gridTemplateColumns: `${HOUR_LABEL_WIDTH_MOBILE}px repeat(7, 1fr)`,
    },
  },
  headerCell: {
    padding: theme.spacing(1, 0),
    textAlign: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderLeft: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
  },
  headerCellFirst: {
    borderLeft: 'none',
  },
  headerToday: {
    '& $dayNumber': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
  dayName: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    letterSpacing: 0.5,
    [theme.breakpoints.only('xs')]: {
      fontSize: 10,
      letterSpacing: 0.25,
    },
  },
  dayNumber: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 500,
    marginTop: 4,
    [theme.breakpoints.only('xs')]: {
      width: 24,
      height: 24,
      fontSize: 12,
      marginTop: 2,
    },
  },
  hourLabelCol: {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  hourLabel: {
    height: HOUR_HEIGHT,
    boxSizing: 'border-box',
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
    fontSize: 11,
    color: theme.palette.text.secondary,
    textAlign: 'right',
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.only('xs')]: {
      height: HOUR_HEIGHT_MOBILE,
      paddingRight: theme.spacing(0.5),
      paddingTop: 2,
      fontSize: 10,
    },
  },
  hourLabelFirst: {
    borderTop: 'none',
  },
  dayCol: {
    position: 'relative',
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  hourCell: {
    height: HOUR_HEIGHT,
    boxSizing: 'border-box',
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.only('xs')]: {
      height: HOUR_HEIGHT_MOBILE,
    },
  },
  hourCellInteractive: {
    cursor: 'pointer',
    outline: 'none',
    transition:
      'background-color 100ms ease-in-out, box-shadow 100ms ease-in-out',
    '&:hover, &:focus-visible': {
      backgroundColor: theme.palette.action.hover,
      boxShadow: `inset 0 0 0 1px ${theme.palette.grey[400]}`,
      borderRadius: 4,
      '& $plusIcon': {
        opacity: 0.85,
      },
    },
  },
  hourCellFirst: {
    borderTop: 'none',
  },
  plusIcon: {
    fontSize: 16,
    opacity: 0,
    color: theme.palette.text.secondary,
    pointerEvents: 'none',
    transition: 'opacity 100ms ease-in-out',
    [theme.breakpoints.only('xs')]: {
      fontSize: 14,
    },
  },
  block: {
    position: 'absolute',
    left: 4,
    right: 4,
    borderRadius: 4,
    padding: theme.spacing(0.5, 0.75),
    overflow: 'hidden',
    cursor: 'default',
    fontSize: 11,
    lineHeight: 1.2,
    [theme.breakpoints.only('xs')]: {
      left: 1,
      right: 1,
      padding: '2px 3px',
      fontSize: 9,
      lineHeight: 1.1,
    },
  },
  recurring: {
    backgroundColor: '#E3F2FD',
    border: '1px solid #90CAF9',
    color: theme.palette.text.primary,
  },
  override: {
    backgroundColor: '#FFFFFF',
    backgroundImage:
      'repeating-linear-gradient(135deg, #E3F2FD 0 6px, #FFFFFF 6px 12px)',
    border: '1px solid #90CAF9',
    color: theme.palette.text.primary,
  },
  blocked: {
    backgroundColor: '#F5F5F5',
    border: '1px solid #BDBDBD',
    color: theme.palette.text.primary,
  },
  syncedBlocked: {
    backgroundColor: '#FFFFFF',
    backgroundImage:
      'repeating-linear-gradient(135deg, #EEEEEE 0 6px, #FFFFFF 6px 12px)',
    border: '1px dashed #BDBDBD',
    color: theme.palette.text.primary,
  },
  syncedIcon: {
    fontSize: 12,
    verticalAlign: 'middle',
    marginRight: 2,
    color: theme.palette.text.secondary,
  },
}))

const formatBlockTimeFractional = h => {
  const hour24 = Math.floor(h)
  const minute = Math.round((h - hour24) * 60)
  const period = hour24 >= 12 ? 'PM' : 'AM'
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
  return minute === 0
    ? `${hour12}\u00A0${period}`
    : `${hour12}:${String(minute).padStart(2, '0')}\u00A0${period}`
}

const Block = ({
  start,
  end,
  variant,
  label,
  synced,
  hourHeight,
  classes,
  gridStartHour,
  gridEndHour,
}) => {
  const clampedStart = Math.max(start, gridStartHour)
  const clampedEnd = Math.min(end, gridEndHour)
  if (clampedEnd <= clampedStart) return null
  const top = (clampedStart - gridStartHour) * hourHeight
  const height = (clampedEnd - clampedStart) * hourHeight
  return (
    <div
      className={`${classes.block} ${classes[variant]}`}
      style={{ top, height }}
      onClick={e => e.stopPropagation()}
    >
      {synced && <SyncAltIcon className={classes.syncedIcon} />}
      {label ||
        `${formatBlockTimeFractional(start)} – ${formatBlockTimeFractional(
          end,
        )}`}
    </div>
  )
}

const AvailabilityCalendarGrid = ({
  weekRange,
  recurringTimeSlots,
  overrides,
  blocks,
  timeZone,
  onCellClick,
}) => {
  const classes = useStyles()
  const isMobile = useIsMobile()
  const hourHeight = isMobile ? HOUR_HEIGHT_MOBILE : HOUR_HEIGHT
  const recurringByDay = buildRecurringByDayIndex(recurringTimeSlots)
  const overridesByDay = buildOverridesByDateKey(overrides, weekRange.start)
  const blockedByDay = buildBlockedByDay(blocks, weekRange.start, timeZone)
  const { startHour, endHour } = computeVisibleHourRange(
    recurringByDay,
    overridesByDay,
    blockedByDay,
  )
  const today = new Date().toISOString().slice(0, 10)
  const todayStart = DateTime.local().startOf('day')
  const hours = []
  for (let h = startHour; h < endHour; h++) hours.push(h)

  const activateCell = (dayIdx, hour, anchorPosition) => {
    if (!onCellClick) return
    const dayDate = weekRange.start.plus({ days: dayIdx })
    if (dayDate.startOf('day') < todayStart) return
    const startTime = dayDate.set({
      hour,
      minute: 0,
      second: 0,
      millisecond: 0,
    })
    onCellClick({
      date: dayDate.startOf('day'),
      startTime,
      anchorPosition,
    })
  }

  const handleCellClick = (dayIdx, hour) => e => {
    activateCell(dayIdx, hour, { left: e.clientX, top: e.clientY })
  }

  const handleCellKeyDown = (dayIdx, hour) => e => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    activateCell(dayIdx, hour, {
      left: rect.left + rect.width / 2,
      top: rect.top + rect.height / 2,
    })
  }

  return (
    <Box className={classes.grid}>
      <Box className={`${classes.headerCell} ${classes.headerCellFirst}`} />
      {dayLabels.map((label, i) => {
        const dayDate = weekRange.start.plus({ days: i })
        const isToday = dayDate.toISODate() === today
        return (
          <Box
            key={label}
            className={`${classes.headerCell} ${
              isToday ? classes.headerToday : ''
            }`}
          >
            <Typography component="div" className={classes.dayName}>
              {label}
            </Typography>
            <Box component="span" className={classes.dayNumber}>
              {dayDate.day}
            </Box>
          </Box>
        )
      })}

      <Box className={classes.hourLabelCol}>
        {hours.map((h, idx) => (
          <Box
            key={h}
            className={`${classes.hourLabel} ${
              idx === 0 ? classes.hourLabelFirst : ''
            }`}
          >
            {formatHourLabel(h)}
          </Box>
        ))}
      </Box>

      {dayLabels.map((label, dayIdx) => {
        const blocksForDay = blockedByDay[dayIdx]
        const allDayBlocks = blocksForDay.filter(seg => seg.allDay)
        const timedBlocks = blocksForDay.filter(seg => !seg.allDay)
        const dayDate = weekRange.start.plus({ days: dayIdx })
        const isPastDay = dayDate.startOf('day') < todayStart
        return (
          <Box key={label} className={classes.dayCol}>
            {hours.map((h, idx) => {
              const cellLabel = `Add time at ${formatHourLabel(
                h,
              )} on ${dayDate.toFormat('EEEE, MMMM d')}`
              return (
                <Box
                  key={h}
                  className={`${classes.hourCell} ${
                    idx === 0 ? classes.hourCellFirst : ''
                  } ${isPastDay ? '' : classes.hourCellInteractive}`}
                  onClick={isPastDay ? undefined : handleCellClick(dayIdx, h)}
                  onKeyDown={
                    isPastDay ? undefined : handleCellKeyDown(dayIdx, h)
                  }
                  role={isPastDay ? undefined : 'button'}
                  tabIndex={isPastDay ? undefined : 0}
                  aria-label={isPastDay ? undefined : cellLabel}
                >
                  {!isPastDay && (
                    <AddOutlinedIcon className={classes.plusIcon} />
                  )}
                </Box>
              )
            })}
            {allDayBlocks.map((seg, i) => {
              const isSynced = seg.block?.id === '0'
              return (
                <Block
                  key={`b-allday-${i}`}
                  start={startHour}
                  end={endHour}
                  variant={isSynced ? 'syncedBlocked' : 'blocked'}
                  synced={isSynced}
                  label="All day"
                  hourHeight={hourHeight}
                  classes={classes}
                  gridStartHour={startHour}
                  gridEndHour={endHour}
                />
              )
            })}
            {recurringByDay[dayIdx].map((seg, i) => (
              <Block
                key={`r-${i}`}
                start={seg.start}
                end={seg.end}
                variant="recurring"
                hourHeight={hourHeight}
                classes={classes}
                gridStartHour={startHour}
                gridEndHour={endHour}
              />
            ))}
            {overridesByDay[dayIdx].map((seg, i) => (
              <Block
                key={`o-${i}`}
                start={seg.start}
                end={seg.end}
                variant="override"
                hourHeight={hourHeight}
                classes={classes}
                gridStartHour={startHour}
                gridEndHour={endHour}
              />
            ))}
            {timedBlocks.map((seg, i) => {
              const isSynced = seg.block?.id === '0'
              return (
                <Block
                  key={`b-timed-${i}`}
                  start={seg.start}
                  end={seg.end}
                  variant={isSynced ? 'syncedBlocked' : 'blocked'}
                  synced={isSynced}
                  hourHeight={hourHeight}
                  classes={classes}
                  gridStartHour={startHour}
                  gridEndHour={endHour}
                />
              )
            })}
          </Box>
        )
      })}
    </Box>
  )
}

export default AvailabilityCalendarGrid
